import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Card, Select, Modal, Badge } from "../components/ui";
import { APP_FULL, TL, appTitle } from "../i18n";
import { RefreshCw, Download, Upload, Wifi, HardDriveDownload, Clock, Lock, Cloud, HardDrive, ExternalLink, LifeBuoy, ShieldCheck, KeyRound, Eye, EyeOff } from "lucide-react";
import { LiveBackupPanel } from "../components/LiveBackup";
import { encryptBackup, decryptBackup, isEncryptedBackup, scorePassphrase } from "../lib/e2e";

const APPS = [
  { v: "family_tree", l: "Family Tree" },
  { v: "holdings", l: "Holdings Management" },
  { v: "currency", l: "Currency Management" },
  { v: "benefits", l: "Benefits Management" },
];
const INTERVALS = [
  { v: "0", l: "Manual only" },
  { v: "15", l: "Every 15 minutes" },
  { v: "60", l: "Hourly" },
  { v: "1440", l: "Daily" },
];

export default function SyncBackup() {
  const { isUnlocked } = useApp();
  const available = APPS.filter((a) => isUnlocked(a.v));
  const [app, setApp] = useState(available[0]?.v || "");
  const [msg, setMsg] = useState("");
  const [lastSync, setLastSync] = useState(null);
  const [warnOpen, setWarnOpen] = useState(false);
  const [interval, setIntervalPref] = useState(() => localStorage.getItem("sync_interval") || "0");
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreData, setRestoreData] = useState(null);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [dest, setDest] = useState(() => localStorage.getItem("backup_dest") || "local");
  const [cloudOpen, setCloudOpen] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [repairOptIn, setRepairOptIn] = useState(false);
  // E2E encryption
  const [encEnabled, setEncEnabled] = useState(() => localStorage.getItem("backup_encrypt") === "1");
  const [encModal, setEncModal] = useState(false);
  const [encPass, setEncPass] = useState("");
  const [encPass2, setEncPass2] = useState("");
  const [encBusy, setEncBusy] = useState(false);
  const [pendingEnc, setPendingEnc] = useState(null);
  const [decModal, setDecModal] = useState(false);
  const [decPass, setDecPass] = useState("");
  const [decBusy, setDecBusy] = useState(false);
  const [pendingDec, setPendingDec] = useState(null);
  const setEnc = (v) => { setEncEnabled(v); localStorage.setItem("backup_encrypt", v ? "1" : "0"); };
  // Recovery hint (never the passphrase)
  const [encHint, setEncHint] = useState("");
  const [showEnc, setShowEnc] = useState(false);
  const [showDec, setShowDec] = useState(false);
  const strength = scorePassphrase(encPass);

  useEffect(() => { client.get("/repair/settings").then((r) => setRepairOptIn(!!r.data?.enabled)).catch(() => {}); }, []);
  const toggleRepair = async () => {
    const nv = !repairOptIn;
    try { await client.put("/repair/settings", { enabled: nv }); setRepairOptIn(nv); } catch { /* ignore */ }
  };

  useEffect(() => { if (!app && available[0]) setApp(available[0].v); }, [available, app]);

  const loadStatus = async () => {
    try {
      const { data } = await client.get(`/backup-status`);
      setLastBackup(data[app]?.last_backup || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (app) loadStatus(); }, [app]); // eslint-disable-line react-hooks/exhaustive-deps

  const backupAgeDays = lastBackup ? Math.floor((Date.now() - new Date(lastBackup).getTime()) / 86400000) : null;
  const backupStale = lastBackup === null || (backupAgeDays !== null && backupAgeDays > 30);

  const doSync = async () => {
    setWarnOpen(false); setMsg("Syncing…");
    try {
      const { data } = await client.get(`/sync/${app}`);
      setLastSync(data.server_time);
      localStorage.setItem(`last_synced_${app}`, data.server_time);
      setMsg(`Synced ${data.count} record(s) for ${APP_FULL[app] || app} at ${new Date(data.server_time).toLocaleTimeString()}.`);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const saveBlob = (obj, filename) => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const downloadBackup = async () => {
    setMsg("");
    try {
      const { data } = await client.get(`/backup/${app}`);
      if (data.last_backup) setLastBackup(data.last_backup);
      if (encEnabled) {
        // Defer the actual download until the passphrase is confirmed (client-side encryption).
        setPendingEnc(data); setEncPass(""); setEncPass2(""); setEncModal(true);
        return;
      }
      saveBlob(data, `${app}_backup_${new Date().toISOString().slice(0, 10)}.json`);
      if (dest === "cloud") {
        setCloudOpen(true);
        setMsg(`Backup of ${APP_FULL[app] || app} (${data.count} records) downloaded. Follow the steps to upload it to your cloud drive.`);
      } else {
        setMsg(`Backup of ${APP_FULL[app] || app} (${data.count} records) downloaded. Store it on this device, an external drive or a USB stick.`);
      }
    } catch (e) { setMsg(apiErr(e)); }
  };

  const confirmEncryptDownload = async () => {
    if (encPass.length < 8) { setMsg("Passphrase must be at least 8 characters."); return; }
    if (encPass !== encPass2) { setMsg("The two passphrases do not match."); return; }
    setEncBusy(true); setMsg("");
    try {
      const env = await encryptBackup(pendingEnc, encPass, { app: pendingEnc.app, count: pendingEnc.count, exported_at: pendingEnc.exported_at, hint: encHint });
      saveBlob(env, `${app}_backup_${new Date().toISOString().slice(0, 10)}.enc.json`);
      setEncModal(false); setPendingEnc(null); setEncPass(""); setEncPass2(""); setEncHint("");
      if (dest === "cloud") { setCloudOpen(true); setMsg("Encrypted backup downloaded. Upload it to your cloud drive — it is unreadable without your passphrase."); }
      else { setMsg("Encrypted backup downloaded. Keep your passphrase safe — without it the file cannot be recovered."); }
    } catch (e) { setMsg(e.message || "Encryption failed."); } finally { setEncBusy(false); }
  };

  const saveDest = (v) => { setDest(v); localStorage.setItem("backup_dest", v); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (isEncryptedBackup(parsed)) {
          setPendingDec(parsed); setDecPass(""); setDecModal(true);
        } else {
          setRestoreData(parsed); setRestoreOpen(true);
        }
      } catch { setMsg("That file is not a valid backup (JSON expected)."); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const confirmDecrypt = async () => {
    setDecBusy(true); setMsg("");
    try {
      const plain = await decryptBackup(pendingDec, decPass);
      setDecModal(false); setPendingDec(null); setDecPass("");
      setRestoreData(plain); setRestoreOpen(true);
    } catch (e) { setMsg(e.message || "Could not decrypt this file."); } finally { setDecBusy(false); }
  };

  const doRestore = async () => {
    setRestoreOpen(false); setMsg("Restoring…");
    try {
      const { data } = await client.post(`/backup/${app}/restore`, {
        app: restoreData.app, schema_version: restoreData.schema_version,
        records: restoreData.records || [], mode: restoreMode,
      });
      setMsg(`Restore complete: imported ${data.imported} record(s) (${restoreMode}). ${APP_FULL[app] || app} now has ${data.total_now} records.`);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const saveInterval = (v) => { setIntervalPref(v); localStorage.setItem("sync_interval", v); };

  if (available.length === 0) {
    return (
      <div className="space-y-4 fadeup">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><RefreshCw size={24} /> {TL("Sync & Backup")}</h1>
        <LiveBackupPanel />
        <Card className="flex items-center gap-3 text-sm" data-testid="syncbackup-locked">
          <Lock size={18} className="text-[var(--primary)]" />
          Activate an application instance first — sync and backup are available per installed application.
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 fadeup">
      <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><RefreshCw size={24} /> {TL("Sync & Backup")}</h1>
      <p className="text-sm opacity-70 max-w-3xl">{TL("Choose an installed application, then synchronize this device with your other devices that share the same 20-character key, or back up and restore its data. Backups restore safely across older and newer versions of the application.")}</p>

      <LiveBackupPanel />

      <Card className="space-y-3" data-testid="repair-consent-card">
        <div className="flex items-center gap-2 font-head"><LifeBuoy size={18} className="text-[var(--primary)]" /> {TL("Support-assisted restore")}</div>
        <p className="text-sm opacity-70 max-w-3xl">{TL("Off by default — this instance is fully self-managed. When enabled, a support agent can")} <b>{TL("offer")}</b> {TL("a one-click restore of your own data from your live backup if your instance becomes corrupted. It")} <b>{TL("never")}</b> {TL("sends program/code updates, never touches any other account, and nothing happens unless")} <b>{TL("you")}</b> {TL("click to accept. Turn it off any time to stop your app from checking for support offers.")}</p>
        <button type="button" onClick={toggleRepair} data-testid="repair-consent-toggle"
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm lift ${repairOptIn ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-black/15 dark:border-white/15 opacity-80"}`}>
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${repairOptIn ? "bg-[var(--primary)]" : "bg-black/30 dark:bg-white/30"}`} />
          {repairOptIn ? TL("Enabled — support may offer restores") : TL("Disabled (default) — no support access")}
        </button>
      </Card>

      <Card className="flex flex-wrap items-end gap-3">
        <div className="w-72"><Select label={TL("Application")} value={app} onChange={(e) => setApp(e.target.value)} data-testid="sb-app">
          {available.map((a) => <option key={a.v} value={a.v}>{TL(a.l)}</option>)}
        </Select></div>
        {lastSync && <Badge color="info"><Clock size={12} className="inline" /> Last synced {new Date(lastSync).toLocaleString()}</Badge>}
      </Card>

      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="sb-msg">{msg}</Card>}

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="space-y-3">
          <div className="flex items-center gap-2 font-head"><Wifi size={18} className="text-[var(--primary)]" /> {TL("Device sync")}</div>
          <p className="text-sm opacity-70">{TL("Pull the latest data shared by devices using the same instance key. Set how often this device syncs.")}</p>
          <div className="w-56"><Select label={TL("Auto-sync schedule")} value={interval} onChange={(e) => saveInterval(e.target.value)} data-testid="sb-interval">
            {INTERVALS.map((i) => <option key={i.v} value={i.v}>{TL(i.l)}</option>)}
          </Select></div>
          <Button onClick={() => setWarnOpen(true)} data-testid="sb-sync-now"><RefreshCw size={16} /> {TL("Sync now")}</Button>
          <p className="text-xs opacity-50">{TL("Bluetooth / local-WiFi peer-to-peer sync is available in the installed native app.")}</p>
        </Card>

        <Card className="space-y-3">
          <div className="flex items-center gap-2 font-head"><HardDriveDownload size={18} className="text-[var(--primary)]" /> {TL("Backup & restore")}</div>
          <p className="text-sm opacity-70">{TL("Choose where to keep your backup, then download it. You can restore it any time on this or another device.")}</p>

          <div className="flex flex-wrap items-center gap-2" data-testid="sb-backup-status">
            {lastBackup
              ? <Badge color={backupStale ? "warning" : "success"}><Clock size={12} className="inline" /> Last backup {new Date(lastBackup).toLocaleDateString()} ({backupAgeDays === 0 ? "today" : `${backupAgeDays}d ago`})</Badge>
              : <Badge color="warning"><Clock size={12} className="inline" /> {TL("No backup taken yet")}</Badge>}
          </div>
          {backupStale && (
            <div className="rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300" data-testid="sb-backup-nudge">
              {lastBackup
                ? `It has been over 30 days since your last backup of ${APP_FULL[app] || app}. We recommend downloading a fresh backup now.`
                : `${TL("You haven't backed up")} ${appTitle(app)} ${TL("yet. Download a backup to keep your data safe.")}`}
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2">
            <button type="button" onClick={() => saveDest("local")} data-testid="sb-dest-local"
              className={`flex items-start gap-3 rounded-md border p-3 text-start lift ${dest === "local" ? "border-[var(--primary)] ring-1 ring-[var(--primary)]" : "border-black/15 dark:border-white/15"}`}>
              <HardDrive size={18} className="mt-0.5 text-[var(--primary)]" />
              <div><div className="text-sm font-medium">{TL("This device / local drive")}</div>
                <div className="text-xs opacity-60">{TL("Save the file to your computer, external or USB drive.")}</div></div>
            </button>
            <button type="button" onClick={() => saveDest("cloud")} data-testid="sb-dest-cloud"
              className={`flex items-start gap-3 rounded-md border p-3 text-start lift ${dest === "cloud" ? "border-[var(--primary)] ring-1 ring-[var(--primary)]" : "border-black/15 dark:border-white/15"}`}>
              <Cloud size={18} className="mt-0.5 text-[var(--primary)]" />
              <div><div className="text-sm font-medium">{TL("Cloud drive")}</div>
                <div className="text-xs opacity-60">{TL("Google Drive, Dropbox, OneDrive — your account, kept private.")}</div></div>
            </button>
          </div>

          <div className="rounded-md border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-3" data-testid="sb-encrypt-card">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={encEnabled} onChange={(e) => setEnc(e.target.checked)} data-testid="sb-encrypt-toggle" className="mt-0.5" />
              <span>
                <span className="flex items-center gap-1.5 font-medium"><ShieldCheck size={15} className="text-[var(--primary)]" /> {TL("Encrypt backup with a passphrase")} <Badge color="success">{TL("recommended")}</Badge></span>
                <span className="mt-0.5 block text-xs opacity-65">{TL("End-to-end AES-256 with Argon2id key stretching, done entirely on this device — the file is unreadable without your passphrase, and no copy is ever kept on our servers. Keep the passphrase safe: it is yours alone (never shared, escrowed, or split), so if you lose it the backup cannot be recovered.")}</span>
              </span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={downloadBackup} data-testid="sb-download">{encEnabled ? <Lock size={16} /> : <Download size={16} />} {dest === "cloud" ? TL("Download for cloud") : TL("Download backup")}</Button>
            <label className="inline-flex">
              <input type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="sb-restore-file" />
              <span className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-black/15 dark:border-white/15 px-4 py-2 text-sm lift"><Upload size={16} /> {TL("Restore from file…")}</span>
            </label>
          </div>
          {dest === "cloud" && <p className="text-xs opacity-50">{TL("After downloading, you'll get steps to upload the file to your own cloud drive. Your data stays private to your account.")}</p>}
        </Card>
      </div>

      <Modal open={cloudOpen} onClose={() => setCloudOpen(false)} title={TL("Upload your backup to the cloud")} testid="sb-cloud-modal">
        <div className="space-y-4 text-sm">
          <p>{TL("Your backup file was downloaded to this device. Now upload it to your own cloud drive so it is stored privately in your account:")}</p>
          <div className="space-y-2">
            <div className="font-medium">{TL("Google Drive")}</div>
            <ol className="list-decimal ps-5 space-y-1 opacity-80">
              <li>{TL("Open")} <b>{TL("drive.google.com")}</b> {TL("and sign in.")}</li>
              <li>{TL("Click")} <b>{TL("New → File upload")}</b>.</li>
              <li>{TL("Select the file just downloaded (")}<code>{app}_backup_…json</code>).</li>
            </ol>
          </div>
          <div className="space-y-1 opacity-80">
            <div className="font-medium">{TL("Dropbox / OneDrive")}</div>
            <p>{TL("Open the service, use its")} <b>{TL("Upload")}</b> {TL("button, and choose the same file.")}</p>
          </div>
          <p className="text-xs opacity-60">To restore later: download the file from your cloud account, then use "Restore from file" here.</p>
          <div className="flex justify-end gap-2">
            <a href="https://drive.google.com" target="_blank" rel="noopener noreferrer" data-testid="sb-open-drive">
              <Button variant="ghost"><ExternalLink size={16} /> {TL("Open Google Drive")}</Button>
            </a>
            <Button onClick={() => setCloudOpen(false)} data-testid="sb-cloud-done">{TL("Done")}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={warnOpen} onClose={() => setWarnOpen(false)} title={TL("Data usage notice")} testid="sb-warn-modal">
        <div className="space-y-4">
          <p className="text-sm">{TL("Synchronizing will transfer your application data over your network connection and")} <b>{TL("may use mobile data or bandwidth")}</b>{TL(". Continue only on a connection you trust.")}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setWarnOpen(false)}>{TL("Cancel")}</Button>
            <Button onClick={doSync} data-testid="sb-warn-confirm">{TL("Continue & sync")}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={restoreOpen} onClose={() => setRestoreOpen(false)} title={TL("Confirm restore")} testid="sb-restore-modal">
        {restoreData && (
          <div className="space-y-4">
            <div className="text-sm">
              Backup of <b>{APP_FULL[restoreData.app] || restoreData.app}</b> · {(restoreData.records || []).length} record(s)
              {restoreData.schema_version && <> · version {restoreData.schema_version}</>}
              {restoreData.exported_at && <> · {new Date(restoreData.exported_at).toLocaleDateString()}</>}
            </div>
            {restoreData.app && restoreData.app !== app && (
              <div className="text-sm text-[#DA1E28]" data-testid="sb-restore-mismatch">This file is for "{restoreData.app}" but you selected "{app}". Switch the application above first.</div>
            )}
            <Select label={TL("Restore mode")} value={restoreMode} onChange={(e) => setRestoreMode(e.target.value)} data-testid="sb-restore-mode">
              <option value="merge">{TL("Merge — add/update records, keep existing")}</option>
              <option value="replace">{TL("Replace — wipe this app's data, then import")}</option>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRestoreOpen(false)}>{TL("Cancel")}</Button>
              <Button onClick={doRestore} disabled={restoreData.app && restoreData.app !== app} data-testid="sb-restore-confirm">{TL("Restore")}</Button>
            </div>
          </div>
        )}
      </Modal>
      <Modal open={encModal} onClose={() => !encBusy && setEncModal(false)} title={TL("Set a backup passphrase")} testid="sb-enc-modal">
        <div className="space-y-4">
          <p className="text-sm opacity-75">{TL("Your backup will be encrypted on this device before it is saved. Choose a strong passphrase you can remember —")} <b>{TL("there is no way to recover the file without it.")}</b></p>
          <div className="space-y-2">
            <label className="block text-xs font-medium opacity-70">{TL("Passphrase (min. 8 characters)")}</label>
            <div className="relative">
              <input type={showEnc ? "text" : "password"} value={encPass} onChange={(e) => setEncPass(e.target.value)} autoFocus data-testid="sb-enc-pass"
                className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 pe-10 text-sm" />
              <button type="button" onClick={() => setShowEnc((s) => !s)} data-testid="sb-enc-show" aria-label={showEnc ? "Hide passphrase" : "Show passphrase"}
                className="absolute end-2 top-1/2 -translate-y-1/2 opacity-55 hover:opacity-100">{showEnc ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
            {encPass && (
              <div className="flex items-center gap-2" data-testid="sb-enc-strength">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                  <div className={`h-full transition-all ${strength.score <= 1 ? "bg-[#DA1E28]" : strength.score === 2 ? "bg-amber-500" : strength.score === 3 ? "bg-[#F1C21B]" : "bg-[#24A148]"}`} style={{ width: `${strength.pct}%` }} />
                </div>
                <span className="text-xs opacity-70">{strength.label}</span>
              </div>
            )}
            <label className="block text-xs font-medium opacity-70">{TL("Confirm passphrase")}</label>
            <div className="relative">
              <input type={showEnc ? "text" : "password"} value={encPass2} onChange={(e) => setEncPass2(e.target.value)} data-testid="sb-enc-pass2"
                className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 pe-10 text-sm" />
              <button type="button" onClick={() => setShowEnc((s) => !s)} data-testid="sb-enc-show2" aria-label={showEnc ? "Hide passphrase" : "Show passphrase"}
                className="absolute end-2 top-1/2 -translate-y-1/2 opacity-55 hover:opacity-100">{showEnc ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
            <label className="block text-xs font-medium opacity-70">{TL("Recovery hint (optional — never your passphrase)")}</label>
            <input type="text" value={encHint} onChange={(e) => setEncHint(e.target.value)} maxLength={200} placeholder={TL("e.g. our usual travel phrase")} data-testid="sb-enc-hint"
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
            <p className="text-[11px] opacity-55">{TL("The hint is stored in cleartext to jog your memory. Never put the passphrase itself here.")}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEncModal(false)} disabled={encBusy}>{TL("Cancel")}</Button>
            <Button onClick={confirmEncryptDownload} disabled={encBusy} data-testid="sb-enc-confirm">
              {encBusy ? <RefreshCw size={16} className="animate-spin" /> : <Lock size={16} />} {encBusy ? "Encrypting…" : "Encrypt & download"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={decModal} onClose={() => !decBusy && setDecModal(false)} title={TL("Enter backup passphrase")} testid="sb-dec-modal">
        <div className="space-y-4">
          <p className="text-sm opacity-75 flex items-center gap-2"><KeyRound size={16} className="text-[var(--primary)]" /> {TL("This backup is encrypted. Enter the passphrase used when it was created to restore it.")}</p>
          {pendingDec?.hints?.app && <div className="text-xs opacity-60">Encrypted backup{pendingDec.hints.app ? ` · ${APP_FULL[pendingDec.hints.app] || pendingDec.hints.app}` : ""}{pendingDec.hints.count != null ? ` · ${pendingDec.hints.count} record(s)` : ""}{pendingDec.hints.exported_at ? ` · ${new Date(pendingDec.hints.exported_at).toLocaleDateString()}` : ""}</div>}
          {(pendingDec?.hints?.hint) && <div className="rounded-md bg-[var(--primary)]/5 px-3 py-2 text-xs" data-testid="sb-dec-hint"><b>{TL("Hint:")}</b> {pendingDec.hints.hint}</div>}
          <div className="relative">
            <input type={showDec ? "text" : "password"} value={decPass} onChange={(e) => setDecPass(e.target.value)} autoFocus data-testid="sb-dec-pass"
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 pe-10 text-sm" />
            <button type="button" onClick={() => setShowDec((s) => !s)} data-testid="sb-dec-show" aria-label={showDec ? "Hide passphrase" : "Show passphrase"}
              className="absolute end-2 top-1/2 -translate-y-1/2 opacity-55 hover:opacity-100">{showDec ? <EyeOff size={16} /> : <Eye size={16} />}</button>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDecModal(false)} disabled={decBusy}>{TL("Cancel")}</Button>
            <Button onClick={confirmDecrypt} disabled={decBusy || !decPass} data-testid="sb-dec-confirm">
              {decBusy ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />} {decBusy ? "Decrypting…" : "Decrypt & continue"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
